import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { tablesDB, DB_ID, PRODUCTS_TABLE } from "../../appwrite";
import { Query } from "appwrite";
import Product from "./Product";
import { useTitle } from "../../hooks/useTitle";
import "./AllProducts.css";
import Navbar from "../../Components/Navbar/Navbar";
import Footer from "../../Components/Footer/Footer";
import SideBar from "./SideBar";
import "./SideBar.css";
import Sorting from "../Shop/Sorting";
import Loading from "../../Loading";
import { SlidersHorizontal, X } from "lucide-react";

export default function AllProducts() {
  useTitle("All Products - TrendBay");

  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("search") || "";

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [sort, setSort] = useState("newest");
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const pageSize = 12;

  const fetchProducts = async (loadMore = false) => {
    loadMore ? setLoadingMore(true) : setLoading(true);
    try {
      const queries = [];
      if (filters.category && filters.category !== "all")
        queries.push(Query.equal("category", filters.category));
      if (filters.availability)
        queries.push(Query.equal("available", filters.availability === "available"));

      switch (sort) {
        case "price-asc":  queries.push(Query.orderAsc("price")); break;
        case "price-desc": queries.push(Query.orderDesc("price")); break;
        case "popularity": queries.push(Query.orderDesc("popularity")); break;
        default:           queries.push(Query.orderDesc("$createdAt")); break;
      }

      queries.push(Query.limit(pageSize));
      if (loadMore) queries.push(Query.offset(offset));

      const res = await tablesDB.listRows({ databaseId: DB_ID, tableId: PRODUCTS_TABLE, queries });
      const rows = (res.rows || res.documents || []).map((r) => ({ id: r.$id, ...r }));

      if (loadMore) {
        setProducts((prev) => [...prev, ...rows]);
        setFilteredProducts((prev) => [...prev, ...rows]);
      } else {
        setProducts(rows);
        setFilteredProducts(rows);
      }

      setHasMore(rows.length === pageSize);
      setOffset(loadMore ? offset + rows.length : rows.length);
    } catch {}
    finally { loadMore ? setLoadingMore(false) : setLoading(false); }
  };

  useEffect(() => {
    setOffset(0);
    setHasMore(true);
    fetchProducts(false);
  }, [filters.category, filters.availability, sort]);

  useEffect(() => {
    let list = [...products];
    if (searchQuery) list = list.filter((p) => (p.productName || "").toLowerCase().includes(searchQuery.toLowerCase()));
    if (filters.minPrice !== undefined) list = list.filter((p) => p.price >= filters.minPrice);
    if (filters.maxPrice !== undefined) list = list.filter((p) => p.price <= filters.maxPrice);
    switch (sort) {
      case "price-asc":  list.sort((a, b) => a.price - b.price); break;
      case "price-desc": list.sort((a, b) => b.price - a.price); break;
      case "popularity": list.sort((a, b) => (b.popularity || 0) - (a.popularity || 0)); break;
      default:
        list.sort((a, b) => new Date(b.$createdAt || "") - new Date(a.$createdAt || ""));
    }
    setFilteredProducts(list);
  }, [filters.minPrice, filters.maxPrice, products, sort, searchQuery]);

  return (
    <div className="ap-page">
      <Navbar />

      <div className="ap-wrap">
        <nav className="ap-crumb">
          <a href="/">Home</a>
          <span>/</span>
          <a href="/shop">Shop</a>
          <span>/</span>
          <span className="ap-crumb-active">All Products</span>
        </nav>

        <h1 className="ap-title">
          {searchQuery ? `Results for "${searchQuery}"` : "All Products"}
        </h1>

        {/* Mobile filter bar */}
        <div className="ap-mob-filter-bar">
          <button className="ap-mob-filter-bar__btn" onClick={() => setFilterOpen(true)}>
            <SlidersHorizontal size={15} /> Filter
          </button>
          <div className="ap-mob-filter-bar__sort">
            <Sorting onSortChange={setSort} />
          </div>
          <span className="ap-mob-filter-bar__count">{filteredProducts.length} items</span>
        </div>

        <div className="ap-layout">
          <aside className="ap-sidebar">
            <SideBar onFilterChange={(f) => setFilters(f)} />
          </aside>

          <div className="ap-main">
            <div className="ap-toolbar">
              <Sorting onSortChange={setSort} />
            </div>

            {loading ? (
              <div className="ap-loading"><Loading /></div>
            ) : filteredProducts.length > 0 ? (
              <div className="ap-grid">
                {filteredProducts.map((p) => (
                  <Product
                    key={p.id}
                    product_id={p.id}
                    productImage={p.productImage}
                    productName={p.productName}
                    price={p.price}
                    productDescription={p.productDescription}
                    category={p.category}
                  />
                ))}
              </div>
            ) : (
              <div className="ap-empty"><p>No products found.</p></div>
            )}

            {hasMore && !loading && (
              <div className="ap-load-more">
                <button onClick={() => fetchProducts(true)} disabled={loadingMore}>
                  {loadingMore ? "Loading…" : "Load More"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile filter bottom sheet */}
      {filterOpen && (
        <>
          <div className="ap-sheet-backdrop" onClick={() => setFilterOpen(false)} />
          <div className="ap-sheet">
            <div className="ap-sheet-top">
              <div className="ap-sheet-handle" />
              <button className="ap-sheet-close" onClick={() => setFilterOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="ap-sheet-body">
              <SideBar onFilterChange={(f) => setFilters(f)} />
            </div>
            <div className="ap-sheet-footer">
              <button className="ap-sheet-apply" onClick={() => setFilterOpen(false)}>
                Apply Filters
              </button>
            </div>
          </div>
        </>
      )}
      <Footer />
    </div>
  );
}
